import React from 'react'
import { ScrollView, StyleSheet, View } from 'react-native'
import { DS } from '@/design-system'

import { formatIsoDateString } from '@/utils/dateFormatter'
import { formatMs } from '@/utils/timeFormatter'
import { formatChipsNumber } from '@/components/GameAssets/Player/PlayerBox'

import * as cth from '@/router/(root)/cth/cthModule'
import * as d10 from '@/router/(root)/daily10/d10Module'
import * as pocketPick from '@/router/(root)/pocketPick/pocketPickModule'

import { defaultTheme } from '@/components/Mini/theme'


function PocketPickLogCard({ compact=false }){

  const { log } = pocketPick.usePocketPickQueryLog()

  const theme = pocketPick.useStore(s => s.theme)

  const labels = ['Date', 'Rounds', 'Time']

  const data = log?.map(function (item) {
    const key = item.playedDate
    const date = formatIsoDateString(item.playedDate, 'short')
    const rounds = `${item.roundsCorrect}/${item.totalRounds}`
    const time = item.totalElapsedMs != null ? formatMs(item.totalElapsedMs) : '-'
    return { key, values: [date, rounds, time] }
  })

  return <HistoryCard theme={theme} log={log} labels={labels} data={data} compact={compact} />
}

function CthLogCard({ compact=false }){

  const { log } = cth.useCthQueryLog()

  const theme = cth.useStore(s => s.theme)

  const labels = ['Date', 'Rounds', 'Time']

  const data = log?.map(function (item) {
    const key = item.playedDate
    const date = formatIsoDateString(item.playedDate, 'short')
    const rounds = `${item.roundsCorrect}/${item.totalRounds}`
    const time = item.totalElapsedMs != null ? formatMs(item.totalElapsedMs) : '-'
    return { key, values: [date, rounds, time] }
  })

  return <HistoryCard theme={theme} log={log} labels={labels} data={data} compact={compact} />
}

function D10LogCard({ compact = false }){

  const { log } = d10.useD10QueryLog()

  const theme = d10.useStore(s => s.theme)

  const labels = ['Date', 'Hands', 'Score']

  const data = log?.map((item) => {
    const key = item.playedDate
    const date = formatIsoDateString(item.playedDate, 'short')
    const hands = `${item.countedHands}`
    const score = formatChipsNumber(item.finalChips)
    return { key, values: [date, hands, score] }
  })

  return <HistoryCard theme={theme} log={log} labels={labels} data={data} compact={compact} />
}

 type HistoryCardTheme = {
    surface: string
    surfaceBorder: string
    rowBackground: string
    textDefaultDim: string
    accent: string
  }
  
function HistoryCard({theme:T=defaultTheme, log, labels, data, compact=false}: {
  theme?: typeof defaultTheme
  log: any[] | undefined
  labels: string[]
  data: { key: string; values: string[] }[] | undefined
  compact?: boolean
}){

  const theme = {
    surface: T.surface,
    surfaceBorder: T.surfaceBorder,
    rowBackground: T.isDark ? T.rowBackground : T.surface,
    textDefaultDim: T.textDefaultDim,
    accent: T.accent 
  }

  return (
    <View style={[styles.card, !compact ? {flexShrink:1, minHeight: 0 /*maxHeight: "100%"*/} : {}, { backgroundColor: theme.surface, borderColor: theme.surfaceBorder }]}>

      {/* no history yet */}
      {log?.length === 0 &&
      <HistoryMsg msg="No games played yet" theme={theme} />
      }

      {/* some history */}
      {!!log?.length && (
      <View style={{maxHeight: "100%"}}>

        {/*header*/}
        <View style={styles.row}>
          <DS.Text variant="Caption/Bold" style={[styles.colLeft, { color: theme.textDefaultDim }]} uppercase>
            {labels[0]}
          </DS.Text>
          <DS.Text variant="Caption/Bold" style={[styles.colCenter, { color: theme.textDefaultDim }]} uppercase>
            {labels[1]}
          </DS.Text>
          <DS.Text variant="Caption/Bold" style={[styles.colRight, { color: theme.textDefaultDim }]} uppercase>
            {labels[2]}
          </DS.Text>
        </View>

        {/*list*/}
        <ScrollView showsVerticalScrollIndicator={false} scrollEnabled={!compact} contentContainerStyle={styles.listContent}>

          {data?.map((item) => {
            return <HistoryItem key={item.key} item={item} theme={theme} />
          })}

        </ScrollView>

      </View>
      )}

    </View>
  )

}

function HistoryMsg({msg, theme}: { msg: string; theme: HistoryCardTheme }) {
  return (
    <DS.Text style={[styles.historyMsg, { color: theme.textDefaultDim }]} variant="Body Small/Regular" textAlign="center">
      {msg}
    </DS.Text>
  )
}

function HistoryItem({item, theme}: { item: { key: string; values: string[] }; theme: HistoryCardTheme }) {

  const defaultCellVariant = 'Body Medium/Regular'

  return (
    <View style={[styles.itemRow, { backgroundColor: theme.rowBackground }]}>
      <DS.Text variant={defaultCellVariant} style={[styles.colLeft, { color: theme.textDefaultDim }]}>
        {item.values[0]}
      </DS.Text>
      <DS.Text variant={defaultCellVariant} style={[styles.colCenter, { color: theme.accent }]}>
        {item.values[1]}
      </DS.Text>
      <DS.Text variant={defaultCellVariant} style={[styles.colRight, { color: theme.textDefaultDim }]}>
        {item.values[2]}
      </DS.Text>
    </View>
  )
}


const styles = StyleSheet.create({
  card: {
    borderRadius: 20,
    borderWidth: 1,
    padding: 20,
    gap: 8,
    marginHorizontal: 16,
    marginBottom: 16
  },
  row: {
    flexDirection: 'row',
    paddingBottom: 8,
    paddingHorizontal: 8,
  },
  listContent: {
    gap: 8
  },
  itemRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderRadius: 14
  },
  colLeft: {
    flex: 1
  },
  colCenter: {
    flex: 2,
    textAlign: 'center'
  },
  colRight: {
    flex: 1,
    textAlign: 'right'
  },
  historyMsg: {
    padding: DS.spacing[8],
    alignItems: 'center'
  }
})
export { CthLogCard, D10LogCard, PocketPickLogCard }
